import { addToolBreadcrumb } from '@tunasong/models'
import { ChordLib, getCadenceOptions, getCadencePrefix } from '@tunasong/music-lib'
import type { Chord } from '@tunasong/music-lib'
import type { Scale } from '@tunasong/schemas'
import type { FC } from 'react'
import { useEffect } from 'react'
import { CadenceOptionCard } from './cadence-option-card.js'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import styles from './cadence-lab.module.css'

const MIN_CHORDS = 3

/**
 * Footer caption shown only when the scale is modal-no-leading-tone
 * (Mixolydian / Dorian / Phrygian / Aeolian / Locrian). Bowie's §8 Q6.
 * Copy lives in the lexicon as `cadence-modal-no-leading-tone-footer` and
 * resolves at the user's theoryLevel — keeps the lexicon as the single source
 * of truth for pedagogical content.
 */
const MODES_WITHOUT_LEADING_TONE = new Set(['mixolydian', 'dorian', 'phrygian', 'aeolian', 'locrian'])

export interface CadenceLabProps {
  scale: Scale | null | undefined
  chords: Chord[]
  open: boolean
  onClose: () => void
}

/**
 * Cadence Lab drawer. Renders nothing when closed, when the scale is unset,
 * or when the section has fewer than 3 chords (the section-trigger button
 * enforces the latter two as `disabled`; this is a defensive guard at the
 * host level).
 *
 * Spec: docs/superpowers/specs/2026-05-17-cadence-lab-design.md
 */
export const CadenceLab: FC<CadenceLabProps> = props => {
  const { scale, chords, open, onClose } = props
  // Hooks must run unconditionally (React rules-of-hooks + React Compiler).
  // Call useTheoryCopy before the early-return gate; the hook itself is cheap
  // and the result is only consumed when the drawer is open.
  const modalFooterCopy = useTheoryCopy('cadence-modal-no-leading-tone-footer')
  // Polish 6/6 — tool telemetry. Fire only on drawer open (not on every
  // render of the closed state) so the breadcrumb tracks actual usage.
  useEffect(() => {
    if (open) {
      addToolBreadcrumb('opened', { tool: 'cadence-lab' })
    }
  }, [open])
  if (!open || !scale || chords.length < MIN_CHORDS) {
    return null
  }
  const prefix = getCadencePrefix(chords)
  const options = getCadenceOptions(scale)
  const scaleType = scale.type ?? 'major'
  const isMinorKey = scaleType === 'minor'
  const showModalFooter = MODES_WITHOUT_LEADING_TONE.has(scaleType)

  return (
    <div className={styles.drawer} role="dialog" aria-label="Cadence Lab">
      <div className={styles.header}>
        <h2 className={styles.title}>Cadence Lab</h2>
        <button type="button" className={styles.closeButton} aria-label="Close cadence lab" onClick={onClose}>
          ×
        </button>
      </div>
      {showModalFooter && modalFooterCopy?.body ? (
        <p className={styles.modalFooter}>{modalFooterCopy.body}</p>
      ) : null}
      <div className={styles.prefixStrip}>Prefix: {prefix.map(c => ChordLib.getName(c)).join(' → ')}</div>
      <div className={styles.grid}>
        {options.map(option => (
          <CadenceOptionCard key={option.kind} option={option} prefix={prefix} isMinorKey={isMinorKey} />
        ))}
      </div>
    </div>
  )
}
